import { create } from "zustand";
import axios from "axios";

import useAppStore from "./useAppStore";

const API_URL = import.meta.env.VITE_API_BASE_URL;

axios.defaults.withCredentials = true;

const useAuthStore = create((set, get) => ({
    userRole: null,
    loading: true,
    error: null,
    userId: null,
    isHR: false,

    // Forgot password message
    forgotPasswordMessage: null,
    // Forgot password error
    // Za glavne greške,na njih se izbacuje samo modal sa porukom greške
    forgotPasswordError: null,
    // Forgot password loading
    forgotPasswordLoading: false,
    // Forgot password code success
    forgotPasswordCodeSuccess: false,
    // Forgot password code error
    forgotPasswordCodeError: null,
    // Forgot password reset success
    forgotPasswordResetSuccess: false,
    // Forgot password reset error
    forgotPasswordResetError: null,

    // Prvi setup naloga (lozinka + opšti uslovi)
    requiresAccountSetup: false,
    accountSetupToken: null,
    accountSetupEmail: null,
    accountSetupError: null,
    accountSetupLoading: false,

    // Provera sesije
    checkSession: async () => {
        set({ loading: true });
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`);

            const employeesData = useAppStore.getState().employeesData

            if (!employeesData) {
                useAppStore.getState().initializeApp();
            }

            set({ userRole: response.data.userRole, userId: response.data.userId, isHR: response.data.isHR, loading: false });
        } catch (error) {
            set({ userRole: null, isHR: false, loading: false });
        }
    },

    // Login
    login: async (username, password, navigate) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, { username, password });

            if (response.data.requiresSetup) {
                set({
                    requiresAccountSetup: true,
                    accountSetupToken: response.data.setupToken,
                    accountSetupEmail: response.data.email,
                    error: null,
                    accountSetupError: null,
                });
                return 'requiresSetup';
            }

            if (response.data.success) {
                set({
                    userRole: response.data.userRole,
                    error: null,
                    userId: response.data.userId,
                    isHR: response.data.isHR,
                    requiresAccountSetup: false,
                    accountSetupToken: null,
                    accountSetupEmail: null,
                    accountSetupError: null,
                });
                useAppStore.getState().initializeApp();
                navigate(`/${response.data.userRole}`);
                return true;
            }
        } catch (error) {
            set({ error: error.response?.data?.error || 'Login failed' });
            return false;
        }
    },

    clearAccountSetup: () => set({
        requiresAccountSetup: false,
        accountSetupToken: null,
        accountSetupEmail: null,
        accountSetupError: null,
        accountSetupLoading: false,
    }),

    completeAccountSetup: async (password, termsAccepted, navigate) => {
        const setupToken = get().accountSetupToken;

        if (!setupToken) {
            set({ accountSetupError: 'Sesija za podešavanje naloga je istekla. Prijavite se ponovo.' });
            return false;
        }

        set({ accountSetupLoading: true, accountSetupError: null });

        try {
            const response = await axios.post(`${API_URL}/api/auth/complete-account-setup`, {
                setupToken,
                password,
                termsAccepted,
            });

            if (response.data.success) {
                set({
                    userRole: response.data.userRole,
                    userId: response.data.userId,
                    isHR: response.data.isHR,
                    requiresAccountSetup: false,
                    accountSetupToken: null,
                    accountSetupEmail: null,
                    accountSetupError: null,
                    accountSetupLoading: false,
                    error: null,
                });
                useAppStore.getState().initializeApp();
                navigate(`/${response.data.userRole}`);
                return true;
            }

            set({ accountSetupLoading: false });
            return false;
        } catch (error) {
            const responseData = error.response?.data;
            const errorMessage = typeof responseData === 'string'
                ? responseData
                : responseData?.error || 'Podešavanje naloga nije uspelo. Pokušajte ponovo.';
            set({ accountSetupError: errorMessage, accountSetupLoading: false });
            return false;
        }
    },

    // Logout
    logout: async () => {
        try {
            await axios.get(`${API_URL}/api/auth/logout`);
            const resetAppState = useAppStore.getState().resetAppState;
            if (typeof resetAppState === 'function') {
                resetAppState();
            }
            set({
                userRole: null,
                userId: null,
                isHR: false
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
    },

    // Reset error
    resetError: () => set({ error: null }),

    // Forgot password
    forgotPasswordSendMail: async (email) => {
        set({ forgotPasswordLoading: true, forgotPasswordMessage: null });
        try {
            const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
            console.log(response);
            set({ forgotPasswordMessage: { message: response.data.message, email: response.data.email }, forgotPasswordLoading: false });
        } catch (error) {
            const responseData = error.response?.data;
            const errorMessage = typeof responseData === 'string'
                ? responseData
                : responseData?.error || responseData?.message || 'Pokušajte ponovo kasnije ili se obratite administratoru.';
            const forgotPasswordError = { message: errorMessage, status: error.response?.status };
            set({ forgotPasswordError: forgotPasswordError, forgotPasswordLoading: false });
        }
    },

    // Forgot password submit code
    forgotPasswordSubmitCode: async (code) => {
        set({ forgotPasswordLoading: true, forgotPasswordCodeError: null });
        try {
            const response = await axios.post(`${API_URL}/api/auth/forgot-password-submit-code`, { code, email: get().forgotPasswordMessage.email });
            console.log(response);
            if (response.data.success) {
                set({ forgotPasswordCodeSuccess: true, forgotPasswordLoading: false, forgotPasswordCodeError: null });
            } else {
                set({ forgotPasswordLoading: false });
            }
        } catch (error) {
            console.log(error);
            const status = error.response?.status;
            const errorMessage = error.response?.data?.error || error.response?.data?.message;

            if (status === 429) {
                // Glavni limiter (24h zabrana) - setuje glavnu grešku
                const forgotPasswordError = {
                    message: errorMessage || 'Previše neuspešnih pokušaja, pokušajte ponovo za 24 sata, ili kontaktirajte administratora',
                    status: status
                };
                set({ forgotPasswordError: forgotPasswordError, forgotPasswordLoading: false, forgotPasswordCodeError: null });
            } else if (status === 401) {
                // Pogrešan ili istekao kod
                set({ forgotPasswordLoading: false, forgotPasswordCodeError: 'Pogresan ili istekao kod' });
            } else {
                // Sve ostale greške
                set({ forgotPasswordLoading: false, forgotPasswordCodeError: 'Greska, pokusajte ponovo.' });
            }
        }
    },

    // Reset password
    resetPassword: async (newPassword) => {
        set({ forgotPasswordLoading: true, forgotPasswordResetError: null });
        try {
            const response = await axios.post(`${API_URL}/api/auth/reset-password`, { newPassword });
            if (response.data.success) {
                set({ forgotPasswordResetSuccess: true, forgotPasswordLoading: false });
                return true;
            } else {
                set({ forgotPasswordLoading: false });
                return false;
            }
        } catch (error) {
            console.log(error);
            const errorMessage = error.response?.data?.error || 'Greška pri resetovanju lozinke. Pokušajte ponovo.';
            set({ forgotPasswordResetError: errorMessage, forgotPasswordLoading: false, forgotPasswordResetSuccess: false });
            return false;
        }
    },
}));

export default useAuthStore;